import * as React from 'react';
import { FunctionComponent } from 'react';
import { AffectedSchoolList } from './AffectedSchoolList/AffectedSchoolList';
import { GeneralMessagePreviewPush } from '../../../../../components/GeneralMessagePreview/GeneralMessagePreviewPush';
import { GeneralMessagePreviewTabs } from '../../../../../components/GeneralMessagePreview/GeneralMessagePreviewTabs';
import { Switch } from '../../../../../components/Switch/Switch';
import { ScheduleSendingForm } from '../../../../../components/GeneralMessages/ScheduleSendingForm/ScheduleSendingForm';
import { TournamentCustomNotificationPreviewEmail } from './TournamentCustomNotificationPreviewEmail';
import { Attachment } from '../../../../../models/generalMessage';
import '../../../../../components/GeneralMessagePreview/GeneralMessagePreview.css';

interface Props {
  messageEmailSubject: string;
  messageEmailBody: string;
  messagePushTitle: string;
  messagePushBody: string;
  messageAttachments: Attachment[];
  affectedSchools?: any[];
  includeUnionAdmins?: boolean;
  unionName?: string;
  onToggleIncludeUnionAdmins?: (next: boolean) => void;
  submitAction?: 'SEND_NOW' | 'SCHEDULE';
  scheduleAt?: string;
  onSubmitActionChange?: (next: 'SEND_NOW' | 'SCHEDULE') => void;
  onScheduleAtChange?: (next: string) => void;
  isScheduleInvalid?: boolean;
  isEmailEnabled?: boolean;
  isPushEnabled?: boolean;
}

export const TournamentCustomNotificationPreview: FunctionComponent<Props> = props => {
  const {
    messageEmailSubject,
    messageEmailBody,
    messagePushTitle,
    messagePushBody,
    messageAttachments,
    affectedSchools = [],
    includeUnionAdmins,
    unionName,
    onToggleIncludeUnionAdmins,
    submitAction = 'SEND_NOW',
    scheduleAt,
    onSubmitActionChange,
    onScheduleAtChange,
    isEmailEnabled = true,
    isPushEnabled = true
  } = props;

  const previewTabs = [
    ...(isEmailEnabled ? ['Email preview'] : []),
    ...(isPushEnabled ? ['Push preview'] : [])
  ];
  const previewContent = [
    ...(isEmailEnabled
      ? [
          <TournamentCustomNotificationPreviewEmail
            key="email-preview"
            messageEmailSubject={messageEmailSubject}
            messageEmailBody={messageEmailBody}
            messageAttachments={messageAttachments}
          />
        ]
      : []),
    ...(isPushEnabled
      ? [
          <GeneralMessagePreviewPush
            key="push-preview"
            messagePushTitle={messagePushTitle}
            messagePushBody={messagePushBody}
          />
        ]
      : [])
  ];
  const scheduleTimestamp = scheduleAt ? new Date(scheduleAt).getTime() : NaN;
  const isScheduleInPast = Boolean(scheduleAt && !Number.isNaN(scheduleTimestamp) && scheduleTimestamp <= Date.now());

  return (
    <div className="tTournamentNotificationPreview">
      <section className="tTournamentNotificationPreview__section">
        <div className="tTournamentNotificationPreview__deliveryControls">
          <div className="tTournamentNotificationPreview__deliveryMode">
            <div className="btn-group" role="group" aria-label="Notification delivery mode">
              <button
                type="button"
                className={`btn ${submitAction === 'SEND_NOW' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => onSubmitActionChange?.('SEND_NOW')}
              >
                Send now
              </button>

              <button
                type="button"
                className={`btn ${submitAction === 'SCHEDULE' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => onSubmitActionChange?.('SCHEDULE')}
              >
                Schedule for later
              </button>
            </div>
          </div>

          {submitAction === 'SCHEDULE' && (
            <div className="tTournamentNotificationPreview__scheduleBox is-inline">
              <div className="tTournamentNotificationPreview__scheduleField">
                <div className="tTournamentNotificationPreview__schedulePicker">
                  <ScheduleSendingForm
                    hideLabel
                    onScheduleDateChange={event => {
                      onScheduleAtChange?.(event.target.value);
                    }}
                  />
                </div>

                {isScheduleInPast && (
                  <div className="tTournamentNotificationPreview__scheduleError">
                    Date and time must be in the future
                  </div>
                )}
              </div>
            </div>
          )}

          {typeof includeUnionAdmins === 'boolean' && (
            <div className="tTournamentNotificationPreview__unionSwitch is-inline">
              <Switch
                name="include-union-admins-preview"
                value={includeUnionAdmins}
                onChange={(e: any) => onToggleIncludeUnionAdmins?.(!!e?.target?.checked)}
                text={`Include ${unionName ?? 'union'} admins`}
              />
            </div>
          )}
        </div>
      </section>

      <section className="tTournamentNotificationPreview__section">
        <div className="tTournamentNotificationPreview__sectionHeading">
          <h3>Message preview</h3>
          <p>Check how the message will appear in each enabled channel.</p>
        </div>
        <div className="tTournamentNotificationPreview__box">
          <div className="bGeneralMessagePreview">
            <div>
              <GeneralMessagePreviewTabs key={previewTabs.join('|')} tabs={previewTabs}>
                {previewContent}
              </GeneralMessagePreviewTabs>
            </div>

            <div className="tTournamentNotificationPreview__recipients">
              <AffectedSchoolList affectedSchools={affectedSchools} />
              {typeof includeUnionAdmins === 'boolean' && (
                <div
                  className={`tTournamentNotificationPreview__unionHint ${includeUnionAdmins ? 'is-on' : 'is-off'}`}
                  role="status"
                >
                  {includeUnionAdmins
                    ? `Union admins (${unionName ?? 'union'}) will be included in the receiver list.`
                    : `Union admins (${unionName ?? 'union'}) are excluded from the receiver list.`}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

TournamentCustomNotificationPreview.displayName = 'TournamentCustomNotificationPreview';
